import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { UnitAutocomplete } from '../../../components/UnitAutocomplete';
import { useGraph, useLastResult, useSimulationStore } from '../../../store/simulationStore';
import { useActiveScenario, useScenarioStore, useScenarios } from '../../../store/scenarioStore';
import { FormulaInput } from './FormulaInput';
import type {
  DistributionConfig,
  DistributionKind,
  Scenario,
  SimulationEdge,
  SimulationNode,
  SimulationResultPayload,
  SummaryStatistics
} from '../../../models/simulation';
import { topologicalSort } from '../../../utils/graph';
import { extractVariableNames } from '../../../utils/expressionParser';
import { deriveFormulaUnit } from '../../../utils/formulaUnits';
import { parseNumber } from '../../../utils/numberFormatting';
import { parseUnitFromName } from '../../../utils/units';

type Statistic = keyof SummaryStatistics | 'value';
type SortDirection = 'asc' | 'desc';
type SheetTab = 'distribution' | 'formula';

type ColumnId =
  | 'name'
  | 'definition'
  | 'value'
  | 'unit'
  | 'mean'
  | 'median'
  | 'p10'
  | 'p90'
  | 'std'
  | 'distribution'
  | 'min'
  | 'mode'
  | 'max'
  | 'dependencies'
  | 'output'
  | 'statistic'
  | 'tags'
  | 'description'
  | 'settings';

type SheetColumn = { id: ColumnId; label: string; width: number };

interface ModelSheetViewProps {
  onOpenNodeOptions: (nodeId: string) => void;
}

type ModelSheetRow = {
  id: string;
  node: SimulationNode;
  name: string;
  type: 'distribution' | 'formula';
  definition: string;
  value: number | null;
  unit?: string;
  summary?: SummaryStatistics;
  distribution: string;
  min: number | null;
  mode: number | null;
  max: number | null;
  dependencies: string[];
  output: boolean;
  statistic: Statistic;
  tags: string[];
  description: string;
};

const DISTRIBUTION_COLUMNS: SheetColumn[] = [
  { id: 'name', label: 'Name', width: 260 },
  { id: 'definition', label: 'Definition', width: 400 },
  { id: 'value', label: 'Value', width: 112 },
  { id: 'unit', label: 'Unit', width: 128 },
  { id: 'mean', label: 'Mean', width: 112 },
  { id: 'median', label: 'Median', width: 112 },
  { id: 'p10', label: 'P10', width: 112 },
  { id: 'p90', label: 'P90', width: 112 },
  { id: 'std', label: 'Std Dev', width: 112 },
  { id: 'distribution', label: 'Distribution', width: 132 },
  { id: 'min', label: 'Min', width: 112 },
  { id: 'mode', label: 'Mode', width: 112 },
  { id: 'max', label: 'Max', width: 112 },
  { id: 'output', label: 'Output', width: 96 },
  { id: 'statistic', label: 'Statistic', width: 138 },
  { id: 'tags', label: 'Tags', width: 190 },
  { id: 'description', label: 'Description', width: 300 },
  { id: 'settings', label: 'Settings', width: 92 }
];

const FORMULA_COLUMNS: SheetColumn[] = [
  { id: 'name', label: 'Name', width: 260 },
  { id: 'definition', label: 'Formula', width: 520 },
  { id: 'value', label: 'Value', width: 112 },
  { id: 'unit', label: 'Unit', width: 128 },
  { id: 'mean', label: 'Mean', width: 112 },
  { id: 'median', label: 'Median', width: 112 },
  { id: 'p10', label: 'P10', width: 112 },
  { id: 'p90', label: 'P90', width: 112 },
  { id: 'std', label: 'Std Dev', width: 112 },
  { id: 'dependencies', label: 'Inputs', width: 320 },
  { id: 'output', label: 'Output', width: 96 },
  { id: 'statistic', label: 'Statistic', width: 138 },
  { id: 'tags', label: 'Tags', width: 190 },
  { id: 'description', label: 'Description', width: 300 },
  { id: 'settings', label: 'Settings', width: 92 }
];

const NUMERIC_COLUMNS = new Set<ColumnId>([
  'value',
  'mean',
  'median',
  'p10',
  'p90',
  'std',
  'min',
  'mode',
  'max'
]);

const STATISTIC_OPTIONS: Array<{ value: Statistic; label: string }> = [
  { value: 'mean', label: 'Mean' },
  { value: 'median', label: 'Median' },
  { value: 'p10', label: 'P10' },
  { value: 'p90', label: 'P90' },
  { value: 'std', label: 'Std Dev' },
  { value: 'value', label: 'Raw Samples' }
];

const SHEET_TABS: Array<{ id: SheetTab; label: string }> = [
  { id: 'distribution', label: 'Distributions' },
  { id: 'formula', label: 'Formulas' }
];

const SHEET_TEXTAREA_MIN_HEIGHT = 26;
const SHEET_TEXTAREA_MAX_HEIGHT = 156;

const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '...';
  }

  const abs = Math.abs(value);
  const maximumFractionDigits = abs > 0 && abs < 1 ? 4 : 2;

  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits
  });
};

const formatParameterNumber = (value: number | undefined): string =>
  value === undefined || !Number.isFinite(value) ? '' : formatNumber(value);

const getStatisticValue = (
  summary: SummaryStatistics | undefined,
  statistic: Statistic | undefined
): number | null => {
  if (!summary) {
    return null;
  }

  switch (statistic) {
    case 'median':
      return summary.median;
    case 'p10':
      return summary.p10;
    case 'p90':
      return summary.p90;
    case 'std':
      return summary.std;
    case 'value':
    case 'mean':
    default:
      return summary.mean;
  }
};

const getDistributionValueFallback = (config: DistributionConfig): number => {
  switch (config.kind) {
    case 'fixed':
      return config.value;
    case 'normal':
      return config.mean;
    case 'triangular':
      return config.mode;
    case 'uniform':
      return (config.min + config.max) / 2;
  }
};

const getDistributionBounds = (config: DistributionConfig): Pick<ModelSheetRow, 'min' | 'mode' | 'max'> => {
  switch (config.kind) {
    case 'fixed':
      return { min: null, mode: null, max: null };
    case 'normal':
      return { min: config.min ?? null, mode: config.mean, max: config.max ?? null };
    case 'triangular':
      return { min: config.min, mode: config.mode, max: config.max };
    case 'uniform':
      return { min: config.min, mode: null, max: config.max };
  }
};

const getDistributionUnit = (node: SimulationNode): string | undefined => {
  if (node.data.type !== 'distribution') {
    return undefined;
  }

  return node.data.unit ?? parseUnitFromName(node.data.name) ?? undefined;
};

const getDistributionLabel = (config: DistributionConfig): string => {
  switch (config.kind) {
    case 'fixed':
      return 'Fixed';
    case 'normal':
      return 'Normal';
    case 'triangular':
      return 'Triangular';
    case 'uniform':
      return 'Uniform';
  }
};

const formatDistributionDefinition = (config: DistributionConfig): string => {
  switch (config.kind) {
    case 'fixed':
      return `=FIXED(${formatParameterNumber(config.value)})`;
    case 'normal': {
      const bounds = [
        config.min !== undefined ? `min ${formatParameterNumber(config.min)}` : null,
        config.max !== undefined ? `max ${formatParameterNumber(config.max)}` : null
      ].filter(Boolean);
      return `=NORMAL(mean ${formatParameterNumber(config.mean)}, std ${formatParameterNumber(config.std)}${bounds.length > 0 ? `, ${bounds.join(', ')}` : ''})`;
    }
    case 'triangular':
      return `=TRIANGULAR(${formatParameterNumber(config.min)}, ${formatParameterNumber(config.mode)}, ${formatParameterNumber(config.max)})`;
    case 'uniform':
      return `=UNIFORM(${formatParameterNumber(config.min)}, ${formatParameterNumber(config.max)})`;
  }
};

const formatFormulaDefinition = (expression: string): string => {
  const trimmed = expression.trim();
  return trimmed ? `=${trimmed}` : '=';
};

const normalizeTags = (value: string): string[] =>
  value
    .split(',')
    .map((tag) => tag.trim().toLowerCase())
    .filter(Boolean)
    .filter((tag, index, tags) => tags.indexOf(tag) === index);

const getOrderedNodes = (nodes: SimulationNode[], edges: SimulationEdge[]) => {
  try {
    return topologicalSort(nodes, edges);
  } catch {
    return [...nodes].sort((a, b) => {
      const yDelta = a.position.y - b.position.y;
      if (yDelta !== 0) {
        return yDelta;
      }

      const xDelta = a.position.x - b.position.x;
      if (xDelta !== 0) {
        return xDelta;
      }

      return a.data.name.localeCompare(b.data.name, undefined, { sensitivity: 'base' });
    });
  }
};

const getDisplayResult = (
  result: SimulationResultPayload | undefined,
  scenarios: Scenario[],
  activeScenario: Scenario | undefined,
  getScenarioResults: (scenarioId: string) => SimulationResultPayload | undefined
): SimulationResultPayload | undefined => {
  const activeScenarioResult = activeScenario ? getScenarioResults(activeScenario.id) : undefined;
  if (activeScenarioResult) {
    return activeScenarioResult;
  }

  const baselineScenario = scenarios.find((scenario) => scenario.isBaseline);
  const baselineResult = baselineScenario ? getScenarioResults(baselineScenario.id) : undefined;
  return baselineResult ?? result;
};

const getDisplayedNumber = (row: ModelSheetRow, columnId: ColumnId): number | null => {
  const data = row.node.data;

  switch (columnId) {
    case 'value':
      return data.type === 'distribution' && data.config.kind === 'fixed' ? data.config.value : row.value;
    case 'mean':
      return data.type === 'distribution' && data.config.kind === 'normal' ? data.config.mean : row.summary?.mean ?? null;
    case 'median':
      return row.summary?.median ?? null;
    case 'p10':
      return row.summary?.p10 ?? null;
    case 'p90':
      return row.summary?.p90 ?? null;
    case 'std':
      return data.type === 'distribution' && data.config.kind === 'normal' ? data.config.std : row.summary?.std ?? null;
    case 'min':
      return row.min;
    case 'mode':
      return row.mode;
    case 'max':
      return row.max;
    default:
      return null;
  }
};

const getCellText = (row: ModelSheetRow, columnId: ColumnId): string => {
  if (NUMERIC_COLUMNS.has(columnId)) {
    const value = getDisplayedNumber(row, columnId);
    return value === null ? '' : String(value);
  }

  switch (columnId) {
    case 'name':
      return row.name;
    case 'definition':
      return row.definition;
    case 'unit':
      return row.unit ?? '';
    case 'distribution':
      return row.distribution;
    case 'dependencies':
      return row.dependencies.join(', ');
    case 'output':
      return row.output ? 'yes' : 'no';
    case 'statistic':
      return row.output ? row.statistic : '';
    case 'tags':
      return row.tags.join(', ');
    case 'description':
      return row.description;
    case 'settings':
      return '';
    default:
      return '';
  }
};

const compareRows = (a: ModelSheetRow, b: ModelSheetRow, columnId: ColumnId): number => {
  if (NUMERIC_COLUMNS.has(columnId)) {
    const aValue = getDisplayedNumber(a, columnId);
    const bValue = getDisplayedNumber(b, columnId);
    if (aValue === null && bValue === null) return 0;
    if (aValue === null) return 1;
    if (bValue === null) return -1;
    return aValue - bValue;
  }

  return getCellText(a, columnId).localeCompare(getCellText(b, columnId), undefined, {
    numeric: true,
    sensitivity: 'base'
  });
};

interface EditableTextCellProps {
  value: string;
  inputId: string;
  ariaLabel: string;
  className?: string;
  onCommit: (value: string) => void;
}

const EditableTextCell = ({ value, inputId, ariaLabel, className = '', onCommit }: EditableTextCellProps) => {
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const registerActiveInput = useSimulationStore((state) => state.registerActiveInput);
  const unregisterActiveInput = useSimulationStore((state) => state.unregisterActiveInput);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    if (!isFocused) {
      textarea.style.height = `${SHEET_TEXTAREA_MIN_HEIGHT}px`;
      textarea.style.overflowY = 'hidden';
      return;
    }

    textarea.style.height = 'auto';
    const nextHeight = Math.min(
      Math.max(textarea.scrollHeight, SHEET_TEXTAREA_MIN_HEIGHT),
      SHEET_TEXTAREA_MAX_HEIGHT
    );
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > SHEET_TEXTAREA_MAX_HEIGHT ? 'auto' : 'hidden';
  }, [isFocused, localValue]);

  const commit = useCallback(() => {
    unregisterActiveInput(inputId);
    if (localValue !== value) {
      onCommit(localValue);
    }
  }, [inputId, localValue, onCommit, unregisterActiveInput, value]);

  return (
    <textarea
      ref={textareaRef}
      rows={1}
      wrap={isFocused ? 'soft' : 'off'}
      className={`model-sheet-cell-input model-sheet-cell-textarea ${isFocused ? 'is-expanded' : ''} ${className}`}
      value={localValue}
      onChange={(event) => setLocalValue(event.currentTarget.value)}
      onFocus={() => {
        setIsFocused(true);
        registerActiveInput(inputId);
      }}
      onBlur={() => {
        setIsFocused(false);
        commit();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        }
        if (event.key === 'Escape') {
          setLocalValue(value);
          event.currentTarget.blur();
        }
      }}
      aria-label={ariaLabel}
    />
  );
};

interface EditableNumberCellProps {
  value: number | null | undefined;
  inputId: string;
  ariaLabel: string;
  allowEmpty?: boolean;
  fallback?: number;
  onCommit: (value: number | null) => void;
}

const EditableNumberCell = ({
  value,
  inputId,
  ariaLabel,
  allowEmpty = false,
  fallback = 0,
  onCommit
}: EditableNumberCellProps) => {
  const [localValue, setLocalValue] = useState(value === null || value === undefined ? '' : String(value));
  const registerActiveInput = useSimulationStore((state) => state.registerActiveInput);
  const unregisterActiveInput = useSimulationStore((state) => state.unregisterActiveInput);

  useEffect(() => {
    setLocalValue(value === null || value === undefined ? '' : String(value));
  }, [value]);

  const commit = useCallback(() => {
    unregisterActiveInput(inputId);
    const trimmed = localValue.trim();

    if (!trimmed) {
      onCommit(allowEmpty ? null : fallback);
      return;
    }

    onCommit(parseNumber(trimmed, fallback));
  }, [allowEmpty, fallback, inputId, localValue, onCommit, unregisterActiveInput]);

  return (
    <input
      type="number"
      className="model-sheet-cell-input model-sheet-number-input"
      value={localValue}
      onChange={(event) => setLocalValue(event.currentTarget.value)}
      onFocus={() => registerActiveInput(inputId)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.currentTarget.blur();
        }
        if (event.key === 'Escape') {
          setLocalValue(value === null || value === undefined ? '' : String(value));
          event.currentTarget.blur();
        }
      }}
      aria-label={ariaLabel}
    />
  );
};

interface EditableFormulaCellProps {
  value: string;
  nodeId: string;
  nodeName: string;
  onCommit: (value: string) => void;
}

const EditableFormulaCell = ({ value, nodeId, nodeName, onCommit }: EditableFormulaCellProps) => {
  const [localValue, setLocalValue] = useState(value);
  const registerActiveInput = useSimulationStore((state) => state.registerActiveInput);
  const unregisterActiveInput = useSimulationStore((state) => state.unregisterActiveInput);
  const inputId = `${nodeId}-sheet-expression`;

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const commit = useCallback(() => {
    unregisterActiveInput(inputId);
    if (localValue !== value) {
      onCommit(localValue);
    }
  }, [inputId, localValue, onCommit, unregisterActiveInput, value]);

  return (
    <div className="model-sheet-formula-editor">
      <span className="model-sheet-formula-equals" aria-hidden="true">=</span>
      <FormulaInput
        value={localValue}
        onChange={setLocalValue}
        onFocus={() => registerActiveInput(inputId)}
        onBlur={commit}
        currentNodeId={nodeId}
        rows={1}
        expandOnFocus
        ariaLabel={`Formula expression for ${nodeName}`}
      />
    </div>
  );
};

export const ModelSheetView = ({ onOpenNodeOptions }: ModelSheetViewProps) => {
  const graph = useGraph();
  const result = useLastResult();
  const scenarios = useScenarios();
  const activeScenario = useActiveScenario();
  const getScenarioResults = useScenarioStore((state) => state.getScenarioResults);
  const setSelection = useSimulationStore((state) => state.setSelection);
  const updateNode = useSimulationStore((state) => state.updateNode);
  const [sort, setSort] = useState<{ field: ColumnId; direction: SortDirection } | null>(null);
  const [filters, setFilters] = useState<Partial<Record<ColumnId, string>>>({});
  const [activeTab, setActiveTab] = useState<SheetTab>('distribution');

  const displayResult = getDisplayResult(result, scenarios, activeScenario, getScenarioResults);

  const rows = useMemo<ModelSheetRow[]>(() => {
    const orderedNodes = getOrderedNodes(graph.nodes, graph.edges);

    return orderedNodes.map((node) => {
      const nodeResult = displayResult?.results[node.id];
      const summary = nodeResult?.summary;
      const statistic = node.data.statistic ?? 'mean';
      const unit = nodeResult?.unit
        ?? (node.data.type === 'distribution'
          ? getDistributionUnit(node)
          : deriveFormulaUnit(node, graph.nodes) ?? parseUnitFromName(node.data.name) ?? undefined);

      if (node.data.type === 'distribution') {
        const bounds = getDistributionBounds(node.data.config);

        return {
          id: node.id,
          node,
          name: node.data.name,
          type: node.data.type,
          definition: formatDistributionDefinition(node.data.config),
          value: getStatisticValue(summary, statistic) ?? getDistributionValueFallback(node.data.config),
          unit,
          summary,
          distribution: getDistributionLabel(node.data.config),
          ...bounds,
          dependencies: [],
          output: node.data.isOutput === true,
          statistic,
          tags: node.data.tags ?? [],
          description: node.data.description ?? ''
        };
      }

      return {
        id: node.id,
        node,
        name: node.data.name,
        type: node.data.type,
        definition: formatFormulaDefinition(node.data.expression),
        value: getStatisticValue(summary, statistic),
        unit,
        summary,
        distribution: '',
        min: null,
        mode: null,
        max: null,
        dependencies: extractVariableNames(node.data.expression),
        output: node.data.isOutput === true,
        statistic,
        tags: node.data.tags ?? [],
        description: node.data.description ?? ''
      };
    });
  }, [displayResult, graph.edges, graph.nodes]);

  const rowCounts = useMemo(() => ({
    distribution: rows.filter((row) => row.type === 'distribution').length,
    formula: rows.filter((row) => row.type === 'formula').length
  }), [rows]);

  const activeColumns = useMemo(
    () => activeTab === 'distribution' ? DISTRIBUTION_COLUMNS : FORMULA_COLUMNS,
    [activeTab]
  );

  const activeColumnIds = useMemo(
    () => new Set(activeColumns.map((column) => column.id)),
    [activeColumns]
  );

  const activeTableMinWidth = useMemo(
    () => activeColumns.reduce((width, column) => width + column.width, 48),
    [activeColumns]
  );

  const tabRows = useMemo(
    () => rows.filter((row) => row.type === activeTab),
    [activeTab, rows]
  );

  const visibleRows = useMemo(() => {
    const activeFilters = Object.entries(filters)
      .map(([field, value]) => [field as ColumnId, value?.trim().toLowerCase() ?? ''] as const)
      .filter(([field]) => activeColumnIds.has(field))
      .filter(([, value]) => value);

    const filteredRows = activeFilters.length === 0
      ? [...tabRows]
      : tabRows.filter((row) =>
          activeFilters.every(([field, value]) => getCellText(row, field).toLowerCase().includes(value))
        );

    if (!sort || !activeColumnIds.has(sort.field)) {
      return filteredRows;
    }

    return filteredRows.sort((a, b) => {
      const comparison = compareRows(a, b, sort.field);
      if (comparison !== 0) {
        return sort.direction === 'asc' ? comparison : -comparison;
      }

      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }, [activeColumnIds, filters, sort, tabRows]);

  const updateFilter = useCallback((columnId: ColumnId, value: string) => {
    setFilters((current) => ({
      ...current,
      [columnId]: value
    }));
  }, []);

  const toggleSort = useCallback((field: ColumnId) => {
    setSort((current) => {
      if (!current || current.field !== field) {
        return { field, direction: 'asc' };
      }

      if (current.direction === 'asc') {
        return { field, direction: 'desc' };
      }

      return null;
    });
  }, []);

  const openNodeOptions = useCallback((nodeId: string) => {
    setSelection(nodeId);
    onOpenNodeOptions(nodeId);
  }, [onOpenNodeOptions, setSelection]);

  const updateDistributionConfig = useCallback((nodeId: string, next: Record<string, number | undefined>) => {
    updateNode(nodeId, (node) => {
      if (node.data.type !== 'distribution') {
        return;
      }

      node.data.config = { ...node.data.config, ...next } as DistributionConfig;
    });
  }, [updateNode]);

  const setDistributionKind = useCallback((nodeId: string, kind: DistributionKind) => {
    updateNode(nodeId, (node) => {
      if (node.data.type !== 'distribution') {
        return;
      }

      const { label, description } = node.data.config;
      switch (kind) {
        case 'fixed':
          node.data.config = { kind, value: 0, label, description };
          break;
        case 'normal':
          node.data.config = { kind, mean: 0, std: 1, label, description };
          break;
        case 'triangular':
          node.data.config = { kind, min: 0, mode: 0.5, max: 1, label, description };
          break;
        case 'uniform':
          node.data.config = { kind, min: 0, max: 1, label, description };
          break;
      }
    });
  }, [updateNode]);

  const valueStatus = displayResult
    ? `${displayResult.runConfig.trials.toLocaleString()} trials`
    : 'Values pending';

  const activeFilterCount = Object.entries(filters)
    .filter(([field]) => activeColumnIds.has(field as ColumnId))
    .filter(([, value]) => value?.trim()).length;
  const activeTabLabel = activeTab === 'distribution' ? 'distributions' : 'formulas';

  return (
    <section className="model-sheet-page" aria-labelledby="model-sheet-title">
      <div className="model-sheet-shell">
        <header className="model-sheet-header">
          <div>
            <h1 id="model-sheet-title">Model sheet</h1>
          </div>
          <div className="model-sheet-meta" aria-label="Sheet status">
            <span>{visibleRows.length.toLocaleString()} of {tabRows.length.toLocaleString()} {activeTabLabel}</span>
            <span>{valueStatus}</span>
            {activeFilterCount > 0 && (
              <button type="button" className="model-sheet-clear-filters" onClick={() => setFilters({})}>
                Clear filters
              </button>
            )}
          </div>
        </header>

        <div className="model-sheet-tabs" role="tablist" aria-label="Model sheet sections">
          {SHEET_TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={`model-sheet-${tab.id}-panel`}
                id={`model-sheet-${tab.id}-tab`}
                className={`model-sheet-tab ${isActive ? 'is-active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span>{tab.label}</span>
                <span className="model-sheet-tab-count">{rowCounts[tab.id].toLocaleString()}</span>
              </button>
            );
          })}
        </div>

        <div
          className="model-sheet-table-wrap"
          role="tabpanel"
          id={`model-sheet-${activeTab}-panel`}
          aria-labelledby={`model-sheet-${activeTab}-tab`}
        >
          <table className="model-sheet-table" style={{ minWidth: `${activeTableMinWidth}px` }}>
            <colgroup>
              <col className="model-sheet-row-col" />
              {activeColumns.map((column) => (
                <col key={column.id} style={{ width: `${column.width}px` }} />
              ))}
            </colgroup>
            <thead>
              <tr className="model-sheet-headings">
                <th className="model-sheet-row-gutter" scope="col">#</th>
                {activeColumns.map((column) => {
                  const isSorted = sort?.field === column.id;
                  const nextDirection = isSorted && sort.direction === 'asc' ? 'descending' : 'ascending';
                  return (
                    <th key={column.id} scope="col">
                      <button
                        type="button"
                        className="model-sheet-sort-button"
                        onClick={() => toggleSort(column.id)}
                        aria-label={`Sort by ${column.label} ${nextDirection}`}
                      >
                        <span>{column.label}</span>
                        {isSorted && <span className="model-sheet-sort-indicator">{sort.direction === 'asc' ? 'Asc' : 'Desc'}</span>}
                      </button>
                    </th>
                  );
                })}
              </tr>
              <tr className="model-sheet-filter-row">
                <th className="model-sheet-row-gutter" scope="col" aria-label="Filters" />
                {activeColumns.map((column) => (
                  <th key={column.id} scope="col">
                    <input
                      type="search"
                      className="model-sheet-filter-input"
                      value={filters[column.id] ?? ''}
                      onChange={(event) => updateFilter(column.id, event.currentTarget.value)}
                      placeholder="Filter"
                      aria-label={`Filter ${column.label}`}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, index) => {
                const data = row.node.data;

                return (
                  <tr key={row.id}>
                    <th className="model-sheet-row-gutter" scope="row">{index + 1}</th>
                    <th className="model-sheet-name-cell" scope="row">
                      <EditableTextCell
                        value={row.name}
                        inputId={`${row.id}-sheet-name`}
                        ariaLabel={`Node name for ${row.name}`}
                        className="model-sheet-name-input"
                        onCommit={(value) => {
                          updateNode(row.id, (node) => {
                            node.data.name = value;
                          });
                        }}
                      />
                    </th>
                    <td className="model-sheet-definition-cell" title={row.definition}>
                      {data.type === 'formula' ? (
                        <EditableFormulaCell
                          value={data.expression}
                          nodeId={row.id}
                          nodeName={row.name}
                          onCommit={(value) => {
                            updateNode(row.id, (node) => {
                              if (node.data.type === 'formula') {
                                node.data.expression = value;
                              }
                            });
                          }}
                        />
                      ) : (
                        <code tabIndex={0} aria-label={`Distribution definition for ${row.name}`}>
                          {row.definition}
                        </code>
                      )}
                    </td>
                    <td className="model-sheet-number-cell">
                      {data.type === 'distribution' && data.config.kind === 'fixed' ? (
                        <EditableNumberCell
                          value={data.config.value}
                          inputId={`${row.id}-sheet-fixed-value`}
                          ariaLabel={`Fixed value for ${row.name}`}
                          onCommit={(value) => updateDistributionConfig(row.id, { value: value ?? 0 })}
                        />
                      ) : (
                        formatNumber(row.value)
                      )}
                    </td>
                    <td className="model-sheet-unit-cell">
                      {data.type === 'distribution' ? (
                        <div className="model-sheet-unit-editor">
                          <UnitAutocomplete
                            value={row.unit ?? ''}
                            onChange={(value) => {
                              updateNode(row.id, (node) => {
                                if (node.data.type === 'distribution') {
                                  node.data.unit = value || undefined;
                                }
                              });
                            }}
                            placeholder="Type or select"
                            className="model-sheet-cell-input"
                            ariaLabel={`Unit for ${row.name}`}
                          />
                        </div>
                      ) : (
                        row.unit
                      )}
                    </td>
                    <td className="model-sheet-number-cell">
                      {data.type === 'distribution' && data.config.kind === 'normal' ? (
                        <EditableNumberCell
                          value={data.config.mean}
                          inputId={`${row.id}-sheet-normal-mean`}
                          ariaLabel={`Mean for ${row.name}`}
                          onCommit={(value) => updateDistributionConfig(row.id, { mean: value ?? 0 })}
                        />
                      ) : (
                        formatNumber(row.summary?.mean)
                      )}
                    </td>
                    <td className="model-sheet-number-cell">{formatNumber(row.summary?.median)}</td>
                    <td className="model-sheet-number-cell">{formatNumber(row.summary?.p10)}</td>
                    <td className="model-sheet-number-cell">{formatNumber(row.summary?.p90)}</td>
                    <td className="model-sheet-number-cell">
                      {data.type === 'distribution' && data.config.kind === 'normal' ? (
                        <EditableNumberCell
                          value={data.config.std}
                          inputId={`${row.id}-sheet-normal-std`}
                          ariaLabel={`Std dev for ${row.name}`}
                          fallback={1}
                          onCommit={(value) => updateDistributionConfig(row.id, { std: value ?? 1 })}
                        />
                      ) : (
                        formatNumber(row.summary?.std)
                      )}
                    </td>
                    {activeTab === 'distribution' && (
                      <>
                        <td>
                          {data.type === 'distribution' ? (
                            <select
                              className="model-sheet-cell-select"
                              value={data.config.kind}
                              onChange={(event) => setDistributionKind(row.id, event.currentTarget.value as DistributionKind)}
                              aria-label={`Distribution type for ${row.name}`}
                            >
                              <option value="fixed">Fixed</option>
                              <option value="normal">Normal</option>
                              <option value="triangular">Triangular</option>
                              <option value="uniform">Uniform</option>
                            </select>
                          ) : (
                            ''
                          )}
                        </td>
                        <td className="model-sheet-number-cell">
                          {data.type === 'distribution' && (data.config.kind === 'normal' || data.config.kind === 'triangular' || data.config.kind === 'uniform') ? (
                            <EditableNumberCell
                              value={data.config.min ?? null}
                              inputId={`${row.id}-sheet-min`}
                              ariaLabel={`Minimum for ${row.name}`}
                              allowEmpty={data.config.kind === 'normal'}
                              onCommit={(value) => updateDistributionConfig(row.id, { min: value ?? undefined })}
                            />
                          ) : (
                            ''
                          )}
                        </td>
                        <td className="model-sheet-number-cell">
                          {data.type === 'distribution' && data.config.kind === 'triangular' ? (
                            <EditableNumberCell
                              value={data.config.mode}
                              inputId={`${row.id}-sheet-mode`}
                              ariaLabel={`Mode for ${row.name}`}
                              fallback={0.5}
                              onCommit={(value) => updateDistributionConfig(row.id, { mode: value ?? 0.5 })}
                            />
                          ) : data.type === 'distribution' && data.config.kind === 'normal' ? (
                            formatNumber(data.config.mean)
                          ) : (
                            ''
                          )}
                        </td>
                        <td className="model-sheet-number-cell">
                          {data.type === 'distribution' && (data.config.kind === 'normal' || data.config.kind === 'triangular' || data.config.kind === 'uniform') ? (
                            <EditableNumberCell
                              value={data.config.max ?? null}
                              inputId={`${row.id}-sheet-max`}
                              ariaLabel={`Maximum for ${row.name}`}
                              allowEmpty={data.config.kind === 'normal'}
                              fallback={1}
                              onCommit={(value) => updateDistributionConfig(row.id, { max: value ?? undefined })}
                            />
                          ) : (
                            ''
                          )}
                        </td>
                      </>
                    )}
                    {activeTab === 'formula' && (
                      <td
                        className="model-sheet-list-cell"
                        title={row.dependencies.join(', ')}
                        tabIndex={row.dependencies.length > 0 ? 0 : undefined}
                      >
                        {row.dependencies.join(', ')}
                      </td>
                    )}
                    <td>
                      <label className="model-sheet-checkbox-label">
                        <input
                          type="checkbox"
                          checked={row.output}
                          onChange={(event) => {
                            updateNode(row.id, (node) => {
                              node.data.isOutput = event.currentTarget.checked;
                              if (event.currentTarget.checked && !node.data.statistic) {
                                node.data.statistic = 'mean';
                              }
                            });
                          }}
                          aria-label={`Mark ${row.name} as output`}
                        />
                      </label>
                    </td>
                    <td>
                      {row.output ? (
                        <select
                          className="model-sheet-cell-select"
                          value={row.statistic}
                          onChange={(event) => {
                            updateNode(row.id, (node) => {
                              node.data.statistic = event.currentTarget.value as Statistic;
                            });
                          }}
                          aria-label={`Output statistic for ${row.name}`}
                        >
                          {STATISTIC_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      ) : (
                        ''
                      )}
                    </td>
                    <td>
                      <EditableTextCell
                        value={row.tags.join(', ')}
                        inputId={`${row.id}-sheet-tags`}
                        ariaLabel={`Tags for ${row.name}`}
                        onCommit={(value) => {
                          updateNode(row.id, (node) => {
                            const tags = normalizeTags(value);
                            node.data.tags = tags.length > 0 ? tags : undefined;
                          });
                        }}
                      />
                    </td>
                    <td className="model-sheet-description-cell" title={row.description}>
                      <EditableTextCell
                        value={row.description}
                        inputId={`${row.id}-sheet-description`}
                        ariaLabel={`Description for ${row.name}`}
                        onCommit={(value) => {
                          updateNode(row.id, (node) => {
                            node.data.description = value || undefined;
                          });
                        }}
                      />
                    </td>
                    <td className="model-sheet-open-cell">
                      <button
                        type="button"
                        className="model-sheet-open-button"
                        onClick={() => openNodeOptions(row.id)}
                        title={`Open settings for ${row.name}`}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {visibleRows.length === 0 && (
            <div className="model-sheet-empty">
              <p>No {activeTabLabel} match these filters.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
